<template>
  <b-card
    class="overflow-hidden"
    no-body
  >
    <b-table
      responsive
      show-empty
      :busy="loading"
      :items="quotes"
      :fields="computedFields"
      @sort-changed="onSortChanged"
    >
      <template #table-busy>
        <b-skeleton-table
          :rows="4"
          :columns="computedFields.length"
          :table-props="{ small: true }"
        />
      </template>

      <template #empty>
        <table-empty-state
          icon="FileTextIcon"
          :title="emptyTitle"
          :hint="emptyHint"
        />
      </template>

      <template #cell(folio)="data">
        <div class="quote-folio-cell">
          <quote-button-route :quote="data.item" />
          <quote-log-button
            v-if="isAdmin"
            :quote-id="data.item.id"
            :folio="data.item.folio"
          />
        </div>
      </template>
      <template v-slot:[`cell(project_name)`]="data">
        <b-link
          class="font-weight-bold"
          @click="navigateToProject(data.item)"
        >
          {{ data.item.project_name || '-' }}
        </b-link>
      </template>
      <template #cell(published_at)="data">
        {{ data.value | date }}
      </template>
      <template #cell(purchased_at)="data">
        <span v-if="data.value">
          {{ data.value | date }}
        </span>
        <span v-else>-</span>
      </template>
      <template #cell(days_overdue)="data">
        <span v-if="data.item.payment_status === 'payment_approved'">
          0
        </span>
        <span v-else-if="data.item.payment_status === 'credit_approved'">
          {{ calculateDaysOverdue(data.item.purchased_at) }}
        </span>
        <span v-else>-</span>
      </template>
      <template #cell(quote_status)="data">
        <b-badge :variant="$helpers.quoteStatusColor(data.value)">
          {{ $helpers.quoteStatusName(data.value) }}
        </b-badge>
      </template>
      <template #cell(is_express)="data">
        <b-badge
          :id="`tooltip-express-${data.item.id}`"
          pill
          :class="data.item.is_express ? 'bg-express' : 'bg-secondary'"
        >{{
          data.item.is_express
            ? $t('expressdelivery')
            : $t('ordinarydelivery')
        }}</b-badge>
      </template>
      <template #cell(payment_status)="data">
        <b-badge :variant="$helpers.paymentStatusColor(data.value)">
          {{ $helpers.paymentStatusName(data.value) }}
        </b-badge>
      </template>
      <template #cell(is_invoiced)="data">
        <div class="d-flex align-items-center">
          <b-badge
            :variant="$helpers.invoiceStatus(data.item).variant"
            class="w-100"
          >
            {{ $helpers.invoiceStatus(data.item).label }}
          </b-badge>
          <b-button
            variant="primary"
            class="p-0 ml-1 custom-padding"
            @click="
              $router.push({
                name: 'quoteInvoices',
                params: { quoteId: data.item.id },
              })
            "
          >
            <feather-icon
              icon="EyeIcon"
              class="text-white"
              size="18"
              color="#000"
            />
          </b-button>
        </div>
      </template>
      <template #cell(is_supplier_fully_invoiced)="data">
        <b-badge :variant="data.value ? 'success' : 'secondary'">
          {{ data.value ? $t('invoiced') : $t('pending') }}
          {{ data.item.invoiced_purchase_orders_count }} / {{ data.item.total_purchase_orders }}
        </b-badge>
      </template>
      <template #cell(total)="data">
        <span
          v-if="isTotalPending(data.item)"
          class="text-muted"
        >—</span>
        <span v-else>$ {{ data.value | money }}</span>
      </template>
      <template #cell(delivery_status)="data">
        <b-badge :variant="getDeliveryStatusVariant(data.item.delivery_status)">
          <feather-icon
            :icon="getDeliveryIcon(data.item.delivery_status)"
            class="mr-1"
            size="14"
          />
          {{ getDeliveryStatusLabel(data.item.delivery_status) }}
        </b-badge>
      </template>
      <template
        v-if="!noTools"
        #cell(tools)="data"
      >
        <b-dropdown
          :id="`dropdown-tools-${data.item.id}`"
          size="sm"
          dropright
          no-caret
          variant="outline-none"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="18"
              color="#000"
            />
          </template>
          <b-dropdown-item
            @click="$emit('quote-requoted', data.item.id)"
          >
            {{ $t(requoteActionLabelKey(data.item)) }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="canEditNickname"
            @click="openNicknameModal(data.item)"
          >
            {{ $t('editQuoteNickname') }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="canDeleteQuote(data.item)"
            v-b-modal.confirmDelete
            variant="danger"
            @click="quoteToDeleteId = data.item.id"
          >{{ $t('delete') }}</b-dropdown-item>
          <!-- <b-dropdown-item
            :to="{ name: 'editQuote', params: { quoteId: data.item.id } }"
          >{{ $t('edit') }}</b-dropdown-item>
          <b-dropdown-item
            :to="{
              name: 'addProductsToQuote',
              params: { quoteId: data.item.id },
            }"
          >{{ $t('add', { type: $t('products') }) }}</b-dropdown-item> -->
        </b-dropdown>
      </template>
    </b-table>
    <confirmation-modal
      :id="`confirmDelete`"
      :variant="'danger'"
      @confirm="removeQuote()"
    >
      <template v-slot:content>
        {{ $t('deleteQuoteConfirmation') }}
      </template>
    </confirmation-modal>
    <b-modal
      id="editQuoteNicknameModal"
      :title="$t('editQuoteNickname')"
      :ok-title="$t('save')"
      :cancel-title="$t('cancel')"
      :ok-disabled="savingNickname"
      @ok="saveNickname"
      @hidden="resetNicknameModal"
    >
      <b-form-group
        :label="$t('quoteNickname')"
        label-for="quote-nickname-input"
      >
        <b-form-input
          id="quote-nickname-input"
          v-model.trim="nicknameForm.nickname"
          maxlength="120"
          :placeholder="$t('quoteNicknamePlaceholder')"
          autocomplete="off"
        />
      </b-form-group>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BTable,
  BSkeletonTable,
} from 'bootstrap-vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import QuoteButtonRoute from '@/components/pricing/QuoteButtonRoute.vue'
import QuoteLogButton from '@/components/quotes/QuoteLogButton.vue'
import TableEmptyState from '@/components/TableEmptyState.vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import {
  quoteHasFinalizedPurchase as hasFinalizedPurchase,
  requoteActionLabelKey as getRequoteActionLabelKey,
} from '@/utils/quotePurchase'

export default {
  components: {
    BTable,
    BSkeletonTable,
    QuoteButtonRoute,
    QuoteLogButton,
    ConfirmationModal,
    TableEmptyState,
  },
  props: {
    quotes: {
      type: Array,
      default() {
        return []
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyTitle: {
      type: String,
      default: 'Todavía no hay cotizaciones',
    },
    emptyHint: {
      type: String,
      default: '',
    },
    noTools: {
      type: Boolean,
      default() {
        return false
      },
    },
    showProjectColumn: {
      type: Boolean,
      default: false,
    },
    showInvoiceColumn: {
      type: Boolean,
      default: true,
    },
    // Pantalla de ventas del proveedor: la fecha es de venta, no de publicacion, y la
    // factura que le importa es la suya, no la del cliente.
    supplierView: {
      type: Boolean,
      default: false,
    },
    showStatusColumn: {
      type: Boolean,
      default: true,
    },
    showActionsColumn: {
      type: Boolean,
      default: true,
    },
    showPublishedAtColumn: {
      type: Boolean,
      default: true,
    },
    showPurchasedAtColumn: {
      type: Boolean,
      default: false,
    },
    showDaysOverdueColumn: {
      type: Boolean,
      default: false,
    },
    showDeliveryStatusColumn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fields: [
        {
          key: 'folio',
          label: 'Folio',
          sortable: true,
        },
        {
          key: 'is_express',
          label: this.$t('deliveryType'),
        },
        {
          key: 'quote_status',
          label: this.$t('status'),
        },
        {
          key: 'payment_status',
          label: this.$t('paymentStatus'),
        },
        {
          key: 'total',
          label: this.$t('total'),
          sortable: true,
        },
        {
          key: 'tools',
          label: this.$t('actions'),
        },
      ],
      quoteToDeleteId: null,
      quoteToEditNickname: null,
      savingNickname: false,
      nicknameForm: {
        nickname: '',
      },
    }
  },

  computed: {
    ...mapGetters('auth', ['currentUser']),

    canEditNickname() {
      return ['admin', 'customer'].includes(this.currentUser?.role_name)
    },

    isAdmin() {
      return this.currentUser?.role_name === 'admin'
    },

    computedFields() {
      let arr = []

      // 1. Fecha de compra al principio SOLO si viene desde ApprovedQuotes
      if (this.showPurchasedAtColumn) {
        arr.push({
          key: 'purchased_at',
          label: this.$t('purchasedDate'),
          sortable: true,
        })
      }

      // 2. Nueva columna: Estatus de pedido
      if (this.showDeliveryStatusColumn) {
        arr.push({
          key: 'delivery_status',
          label: this.$t('orderStatus'),
        })
      }

      // 3. Resto de columnas igual que antes
      arr.push(
        {
          key: 'folio',
          label: 'Folio',
          sortable: true,
        },
      )

      // Opcional: proyecto
      if (this.showProjectColumn) {
        arr.push({
          key: 'project_name',
          label: this.$t('project'),
        })
      }

      // Fecha de publicación
      if (this.showPublishedAtColumn) {
        arr.push({
          key: 'published_at',
          label: this.supplierView ? 'Fecha de venta' : this.$t('publishedDate'),
          sortable: true,
        })
      }

      // Días vencidos
      if (this.showDaysOverdueColumn) {
        arr.push({
          key: 'days_overdue',
          label: this.$t('daysOverdue'),
        })
      }

      // Resto de columnas
      arr.push(
        {
          key: 'is_express',
          label: this.$t('deliveryType'),
        },
        {
          key: 'quote_status',
          label: this.$t('status'),
        },
        {
          key: 'payment_status',
          label: this.$t('paymentStatus'),
        },
        {
          key: 'total',
          label: this.$t('total'),
          sortable: true,
        },
      )

      // Factura
      if (this.showInvoiceColumn) {
        arr.push(this.supplierView
          ? { key: 'is_supplier_fully_invoiced', label: 'Factura del proveedor' }
          : { key: 'is_invoiced', label: this.$t('customerInvoice') })
      }

      // Acciones
      if (this.showActionsColumn) {
        arr.push({
          key: 'tools',
          label: this.$t('actions'),
        })
      }

      // Ocultar estatus si así se solicita
      if (!this.showStatusColumn) {
        arr = arr.filter(f => f.key !== 'quote_status')
      }

      return arr
    },
  },

  methods: {
    // Una cotizacion sin publicar todavia no tiene precio: un $0.00 ahi se lee como que
    // cuesta cero, no como que falta.
    isTotalPending(item) {
      return !item.published_at && Number(item.total || 0) === 0
    },

    ...mapActions('quotes', ['deleteQuote', 'editQuote', 'fetchQuotes']),
    ...mapMutations('projects', {
      setProject: 'SET_PROJECT',
    }),
    canDeleteQuote(quote) {
      if (quote?.quote_status === 'deleted') return false
      if (this.currentUser?.role_name === 'admin') return true

      return !this.quoteHasFinalizedPurchase(quote)
    },
    quoteHasFinalizedPurchase(quote) {
      return hasFinalizedPurchase(quote)
    },
    requoteActionLabelKey(quote) {
      return getRequoteActionLabelKey(quote)
    },
    getErrorMessage(error) {
      const messages = error?.data?.messages || error?.response?.data?.messages
      if (Array.isArray(messages) && messages.length > 0) return messages[0]
      return this.$t('quoteItemsLockedForAccounting')
    },
    showDeleteError(error) {
      this.$bvToast.toast(this.getErrorMessage(error), {
        title: this.$t('error') || 'Error',
        variant: 'danger',
        solid: true,
      })
    },
    // Se viene de una lista de cotizaciones: lo natural es aterrizar en las cotizaciones de
    // esa obra. Hay que traer la obra completa antes de moverse, porque el guard de la ruta
    // decide con la direccion si la zona tiene cobertura, y con la obra a medias rebotaba a
    // la pantalla anterior.
    async navigateToProject(item) {
      this.setProject({
        id: item.project_id,
        name: item.project_name,
      })

      const project = await this.$store
        .dispatch('projects/ensureProject', item.project_id)
        .catch(() => null)

      const target = this.$helpers.validZone(project?.address) ? 'quotes' : 'groupedExpenses'

      this.$router
        .push({ name: target, params: { projectId: item.project_id } })
        .catch(() => {})
    },
    removeQuote() {
      const deleteReason = window.prompt(
        this.$t('deleteQuoteReasonPrompt') || 'Motivo de eliminación (opcional)',
      )

      if (deleteReason === null) {
        this.quoteToDeleteId = null
        return
      }

      this.deleteQuote({
        quoteId: this.quoteToDeleteId,
        deleteReason,
      }).then(() => {
        this.$emit('quote-deleted')
      }).catch(error => {
        this.showDeleteError(error)
      }).finally(() => {
        this.quoteToDeleteId = null
      })
    },
    openNicknameModal(quote) {
      this.quoteToEditNickname = quote
      this.nicknameForm.nickname = quote?.nickname || ''
      this.$bvModal.show('editQuoteNicknameModal')
    },
    resetNicknameModal() {
      this.quoteToEditNickname = null
      this.savingNickname = false
      this.nicknameForm.nickname = ''
    },
    saveNickname(event) {
      event.preventDefault()
      if (!this.quoteToEditNickname) return

      this.savingNickname = true
      this.editQuote({
        id: this.quoteToEditNickname.id,
        nickname: this.nicknameForm.nickname,
      }).then(() => {
        this.$bvModal.hide('editQuoteNicknameModal')
        this.$emit('quote-nickname-updated')
      }).catch(error => {
        this.$bvToast.toast(this.getErrorMessage(error), {
          title: this.$t('error') || 'Error',
          variant: 'danger',
          solid: true,
        })
      }).finally(() => {
        this.savingNickname = false
      })
    },

    onSortChanged({ sortBy, sortDesc }) {
      this.$emit('sort-changed', {
        field: sortBy,
        direction: sortDesc ? 'desc' : 'asc',
      })
    },

    calculateDaysOverdue(purchasedAt) {
      if (!purchasedAt) return '-'

      const today = new Date()
      const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate())

      const purchasedDate = new Date(purchasedAt)
      const purchasedMidnight = new Date(purchasedDate.getFullYear(), purchasedDate.getMonth(), purchasedDate.getDate())

      const diffInMs = todayMidnight - purchasedMidnight
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

      return -31 + diffInDays
    },
    getDeliveryStatusLabel(status) {
      switch (status) {
        case 'in_warehouse':
          return this.$t('inWarehouse') // En bodega
        case 'on_the_way':
          return this.$t('onTheWay') // En camino
        case 'delivered':
          return this.$t('delivered') // Entregado
        default:
          return this.$t('pending') // Pendiente o sin asignar
      }
    },
    getDeliveryStatusVariant(status) {
      switch (status) {
        case 'in_warehouse':
          return 'primary' // Azul
        case 'on_the_way':
          return 'warning' // Amarillo
        case 'delivered':
          return 'success' // Verde
        default:
          return 'secondary' // Gris por defecto
      }
    },
    getDeliveryIcon(status) {
      switch (status) {
        case 'in_warehouse': return 'PackageIcon'
        case 'on_the_way': return 'TruckIcon'
        case 'delivered': return 'CheckCircleIcon'
        default: return 'ClockIcon'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.quote-folio-cell {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.25rem;
}

.bg-express {
  background-color: #120070;
}
</style>
